import Order, {
  CardOrder,
  OrderInvoices,
  OrderProduct,
  OrderProductStatus,
  OrderStatus,
} from "ts/api/order";
import { getItem } from "tools/localStorage";
import { FullProduct, ProductNoteAttachment } from "ts/api/product";
import { PaymentMethodType } from "ts/api/paymentMethod";
import orderRefs from "api/order/refs";
import OrderReview, {
  CreatedOrderReview,
  OrderReviewStatus,
} from "ts/api/orderReview";
import { addFiles } from "api/helpers";
import { IMAGE_COMPRESSION_MAX_SIZE_MB } from "tools/file/compressImage";
import Transfer from "ts/api/transfer";
import { Currency } from "@evennt-tech/api-v2-core";
import {
  IIncrementCustomProductOptionConfigOverridePurchasedQuantityPayload,
  IIncrementManyProductPurchasesPerStandPayload,
} from "@evennt-tech/api-v2-external";
import firebase, {
  apiV2,
  db,
  FieldValue,
  selectUser,
  storage,
  Timestamp,
} from "../firebase";

type ChangeOrderStatusParams = {
  orderId: string;
  status: OrderProductStatus;
  supplierCompanyId: string;
  showId: string;
  cancelReason?: string;
};

type UploadNotesAttachments = {
  productId: string;
  oldAttachments: ProductNoteAttachment[];
  newAttachments: ProductNoteAttachment[];
  parentProductId?: string;
};

type ChangePurchasedProductQuantityParams = {
  showId: string;
  productId: string;
  incrementPurchasedQuantity: number;
  cpoConfigId?: string;
  supplierCompanyId: string;
};
type MapProductsPurchasedQuantityParams = {
  order: Order;
  status: string;
  showId: string;
};
export type ProceedToPaymentParams =
  | {
      paymentType: PaymentMethodType.cardPayment;
      transfers: Transfer[];
    }
  | {
      paymentType: PaymentMethodType.bankTransfer;
      invoices: OrderInvoices;
      totalAmount?: number;
    }
  | {
      paymentType: PaymentMethodType.idealPayment;
      transfers: Transfer[];
    };

const deleteAttachments = async (
  ref: firebase.storage.Reference,
  attachments: ProductNoteAttachment[]
) =>
  Promise.all(
    attachments.map(async (attachment) =>
      ref
        .child(`${attachment.id}-${attachment.title}`)
        .delete()
        .catch((e) => console.error(e))
    )
  );

/**
 * Files arrive already compressed — `buildNoteAttachment` runs `compressImage`
 * the moment one is picked, so that the size and extension shown in the list
 * describe the bytes that end up here. Compressing again would re-encode an
 * already-lossy JPEG for no gain.
 */
const uploadAttachments = async (
  ref: firebase.storage.Reference,
  attachments: ProductNoteAttachment[]
) => {
  return Promise.all(
    attachments.map(async (attachment) => {
      const file = await ref
        .child(`${attachment.id}-${attachment.title}`)
        .put(attachment.file as File);
      const url = await file.ref.getDownloadURL();
      return { ...attachment, url };
    })
  );
};

export const updateNotesAttachments = async (
  args: UploadNotesAttachments
): Promise<ProductNoteAttachment[]> => {
  const { productId, newAttachments, oldAttachments } = args;
  const path = `productsNotes/${productId}/`;
  const ref = storage.ref(path);
  const deletedAttachments = oldAttachments.filter(
    ({ id: oldId }) => !newAttachments.some(({ id: newId }) => oldId === newId)
  );
  const addedAttachments = newAttachments.filter(
    ({ id: newId }) => !oldAttachments.some(({ id: oldId }) => newId === oldId)
  );
  await deleteAttachments(ref, deletedAttachments);
  const uploadedAddedAttachments = await uploadAttachments(
    ref,
    addedAttachments
  );
  return newAttachments.map((attachment) => {
    const uploadedAddedAttachment = uploadedAddedAttachments.find(
      ({ id }) => id === attachment.id
    );
    if (uploadedAddedAttachment) {
      return uploadedAddedAttachment;
    }
    return attachment;
  });
};

export const removeProductsAttachments = async () => {
  const products = getItem<FullProduct[]>("products", []);

  await Promise.all(
    products.map(async (product) => {
      const ref = storage.ref(`productsNotes/${product.id}/`);
      return deleteAttachments(ref, product.notes?.attachments || []);
    })
  );
};

const mapProductsPurchasedQuantity = async ({
  order,
  status,
  showId,
}: MapProductsPurchasedQuantityParams) => {
  const quantities = order.products.reduce<
    IIncrementCustomProductOptionConfigOverridePurchasedQuantityPayload[]
  >((acc, product) => {
    if (!product.cpoConfig?.id) {
      return acc;
    }

    if (status === OrderProductStatus.cancelled) {
      acc.push({
        cpoConfigId: product.cpoConfig.id,
        incrementBy: -product.quantity,
      });
    } else if (
      product.status === OrderProductStatus.cancelled &&
      status !== OrderProductStatus.cancelled
    ) {
      acc.push({
        cpoConfigId: product.cpoConfig.id,
        incrementBy: product.quantity,
      });
    }
    return acc;
  }, []);

  if (quantities.length) {
    await apiV2.customProductOption.override.incrementQuantities({
      showId,
      values: quantities,
    });
  }
};

export const updateProductsPurchasesPerStand = async (
  order: Order,
  showId: string,
  supplierCompanyId: string,
  shouldIncrement = true
) => {
  const productIds = order.products.map((p) => p.id);
  const productWithLimitIds = await apiV2.product.getIdsWithPurchaseLimit({
    ids: productIds.join(","),
    supplierCompanyId,
  });

  if (!productWithLimitIds.length) return;

  const values = order.products.reduce<
    IIncrementManyProductPurchasesPerStandPayload[]
  >((acc, curr) => {
    if (productWithLimitIds.includes(curr.id)) {
      acc.push({
        supplierCompanyId,
        productId: curr.id,
        incrementBy: shouldIncrement ? curr.quantity : -curr.quantity,
        cpoConfigId: curr.cpoConfig!.id,
      });
    }
    return acc;
  }, []);
  if (values.length) {
    await apiV2.product.purchasePerStand
      .incrementMany({
        showId,
        standId: order.standId,
        values,
      })
      .catch((e) => {
        console.error("Error occurred updating product purchases: ", e);
      });
  }
};

const changeProductsStatus = async (
  showId: string,
  orderId: string,
  status: OrderProductStatus,
  supplierCompanyId?: string
) => {
  const orderRef = db
    .collection("shows")
    .doc(showId)
    .collection("orders")
    .doc(orderId);

  const order = (await orderRef.get()).data() as Order;
  const products = order.products.map((product) => {
    if (supplierCompanyId) {
      if (product.owner.id === supplierCompanyId) {
        const productWithUpdatedStatus = { ...product, status };
        return productWithUpdatedStatus;
      }
      return product;
    }
    return { ...product, status };
  });
  return products;
};
const orderService = {
  update: (showId: string, orderId: string, order: Partial<Order>) => {
    return orderRefs.getDocument({ showId, orderId }).update(order);
  },
  getItem: async (showId: string, orderId: string): Promise<Order> => {
    const orderSnapshot = await orderRefs
      .getDocument({ showId, orderId })
      .get();
    const order = orderSnapshot.data();
    if (!order) {
      throw new Error("No such order");
    }
    return { ...order, id: orderSnapshot.id };
  },
  addItem: async (showId: string, order: Order): Promise<string> => {
    await db
      .collection("shows")
      .doc(showId)
      .collection("orders")
      .doc(order.id)
      .set({ ...order, useNewProcess: true });

    return order.id;
  },
  changeStatus: async (
    showId: string,
    orderId: string,
    status: OrderStatus,
    isPartiallyCancelled: boolean
  ): Promise<void> => {
    const bodyOfUpdate: Record<string, any> = {
      "paymentDetails.status": status,
      isPartiallyCancelled,
    };
    if (isPartiallyCancelled) {
      delete bodyOfUpdate["paymentDetails.status"];
    } else if (status === OrderStatus.cancelled) {
      bodyOfUpdate.cancelledAt = Timestamp.now();
    }
    await orderRefs.getDocument({ showId, orderId }).update(bodyOfUpdate);
  },
  proceedToPayment: async (
    showId: string,
    orderId: string,
    params: ProceedToPaymentParams
  ): Promise<void> => {
    if (params.paymentType === PaymentMethodType.bankTransfer) {
      const { invoices, paymentType, totalAmount } = params;
      const commonParams = {
        invoices,
        paymentType,
      };
      const updatedParams = totalAmount
        ? {
            ...commonParams,
            paymentDetails: {
              amount: totalAmount,
              status: OrderStatus.processing,
            },
          }
        : { ...commonParams, "paymentDetails.status": OrderStatus.processing };
      await orderRefs.getDocument({ showId, orderId }).update(updatedParams);
    }
    const isPaymentCard =
      params.paymentType === PaymentMethodType.cardPayment ||
      params.paymentType === PaymentMethodType.idealPayment;
    if (isPaymentCard) {
      const { transfers, paymentType } = params;
      await orderRefs.getDocument({ showId, orderId }).update({
        transfers,
        paymentType,
        "paymentDetails.status": OrderStatus.processing,
      });
    }
  },
  review: async (
    showId: string,
    order: Order,
    createdReview: CreatedOrderReview,
    currency: Currency,
    supplierCompanyId: string
  ): Promise<void> => {
    const user = selectUser();
    const createdAt = Timestamp.now();
    const path = `shows/${showId}/orders/${order.id}/review/`;
    const attachments = createdReview.attachments
      ? await addFiles({
          files: createdReview.attachments,
          path,
          compressMaxSizeMB: IMAGE_COMPRESSION_MAX_SIZE_MB.orderReview,
        })
      : undefined;
    const review: OrderReview = {
      type: createdReview.type,
      status: createdReview.status,
      reviewer: {
        id: user?.id as string,
        comment: createdReview.comment,
        attachments,
      },
      createdAt,
    } as OrderReview;

    const isBankPaidBeforeApproval =
      order.paymentType === PaymentMethodType.bankTransfer &&
      order.paymentDetails.status === "paid" &&
      order.review?.status === OrderReviewStatus.waitingForApproval;

    let orderStatus = OrderStatus.cancelled;
    if (createdReview.status === OrderReviewStatus.approved) {
      if (order.paymentType === PaymentMethodType.cardPayment) {
        orderStatus = OrderStatus.paid;
      } else if (order.paymentType === PaymentMethodType.bankTransfer) {
        if (order.paymentDetails.status === OrderStatus.paid) {
          orderStatus = OrderStatus.paid;
        } else {
          orderStatus = OrderStatus.reviewCompleted;
        }
      } else if (isBankPaidBeforeApproval) {
        orderStatus = OrderStatus.paid;
      } else {
        orderStatus = OrderStatus.reviewCompleted;
      }
    } else {
      orderStatus = OrderStatus.cancelled;
    }

    const productStatus =
      createdReview.status === OrderReviewStatus.approved
        ? OrderProductStatus.received
        : OrderProductStatus.cancelled;
    const products = await changeProductsStatus(
      showId,
      order.id,
      productStatus
    );

    if (order.paymentType === PaymentMethodType.cardPayment) {
      if (orderStatus === OrderStatus.paid) {
        await apiV2.oldStripe.capturePayment({
          paymentIntent: (order as CardOrder).paymentIntentId as string,
          currency,
          ...((order as CardOrder).useNewProcess && {
            useNewProcess: (order as CardOrder).useNewProcess,
          }),
        });
      } else if (orderStatus === OrderStatus.cancelled) {
        await apiV2.oldStripe.cancelPayment({
          paymentIntent: (order as CardOrder).paymentIntentId as string,
          currency,
          ...((order as CardOrder).useNewProcess && {
            useNewProcess: (order as CardOrder).useNewProcess,
          }),
        });
      }
    }

    // Checkout stamped paymentDate at pre-authorisation; a capture is the
    // moment the money is actually taken, so the date moves with it.
    const isCardCaptured =
      order.paymentType === PaymentMethodType.cardPayment &&
      orderStatus === OrderStatus.paid;
    await orderRefs.getDocument({ showId, orderId: order.id }).update({
      review,
      "paymentDetails.status": orderStatus,
      products,
      ...(isCardCaptured && { "paymentDetails.paymentDate": Timestamp.now() }),
    });

    if (createdReview.status === OrderReviewStatus.rejected) {
      const quantities = order.products.reduce<
        IIncrementCustomProductOptionConfigOverridePurchasedQuantityPayload[]
      >((acc, curr) => {
        if (curr.cpoConfig?.id) {
          acc.push({
            incrementBy: -Math.ceil(curr.quantity),
            cpoConfigId: curr.cpoConfig.id,
          });
        }
        return acc;
      }, []);

      if (quantities.length) {
        await apiV2.customProductOption.override.incrementQuantities({
          showId,
          values: quantities,
        });
      }
      await updateProductsPurchasesPerStand(
        order,
        showId,
        supplierCompanyId,
        false
      );
    }
  },
  deleteItem: async (showId: string, orderId: string): Promise<void> => {
    await db
      .collection("shows")
      .doc(showId)
      .collection("orders")
      .doc(orderId)
      .delete();
  },

  moveToAwaitingForPayment: (showId: string, orderId: string) =>
    db
      .collection("shows")
      .doc(showId)
      .collection("orders")
      .doc(orderId)
      .update({
        "paymentDetails.status": OrderStatus.reviewCompleted,
        paymentType: PaymentMethodType.notSelected,
        transfers: FieldValue.delete(),
      }),

  changeProductsStatus: async (params: ChangeOrderStatusParams) => {
    try {
      const {
        orderId,
        status,
        supplierCompanyId,
        showId,
        cancelReason,
      } = params;

      const orderRef = db
        .collection("shows")
        .doc(showId)
        .collection("orders")
        .doc(orderId);

      const order = (await orderRef.get()).data() as Order;

      const updatedProducts = await changeProductsStatus(
        showId,
        orderId,
        status,
        supplierCompanyId
      );
      const cancelReasonKey = `cancelReason.${supplierCompanyId}`;

      if (order.paymentType === PaymentMethodType.bankTransfer) {
        const invoiceId = Object.values(order.invoices).find((invoice) =>
          invoice.products.find(
            (product) => product.owner.id === supplierCompanyId
          )
        )?.id;
        if (!invoiceId) {
          return;
        }
        const invoiceIsCancelledKey = `invoices.${invoiceId}.isCancelled`;

        if (status === "cancelled") {
          await orderRef.update({
            status,
            products: updatedProducts,
            [cancelReasonKey]: cancelReason,
            [invoiceIsCancelledKey]: true,
          });
        } else {
          await orderRef.update({
            status,
            products: updatedProducts,
            [cancelReasonKey]: FieldValue.delete(),
            [invoiceIsCancelledKey]: false,
          });
        }
      } else if (
        [
          PaymentMethodType.cardPayment,
          PaymentMethodType.idealPayment,
          PaymentMethodType.notSelected,
        ].includes(order.paymentType)
      ) {
        if (status === "cancelled") {
          await orderRef.update({
            status,
            products: updatedProducts,
            [cancelReasonKey]: cancelReason,
          });
        } else {
          await orderRef.update({
            status,
            products: updatedProducts,
            [cancelReasonKey]: FieldValue.delete(),
          });
        }
      }

      await mapProductsPurchasedQuantity({
        order,
        status,
        showId,
      });
      if (status === OrderProductStatus.cancelled) {
        await updateProductsPurchasesPerStand(
          order,
          showId,
          supplierCompanyId,
          false
        );
      }
    } catch (e) {
      throw new Error(`Unexpected error occured updating order status: ${e}`);
    }
  },
  async markOrderProductImagesUsed(products: (OrderProduct | FullProduct)[]) {
    const productFileIds = products.reduce<string[]>((acc, { image }) => {
      if (image?.id) {
        acc.push(image.id);
      }

      return acc;
    }, []);
    if (productFileIds.length) {
      await apiV2.product.file
        .markFileUsed({ fileIds: productFileIds })
        .catch((e) => {
          console.error(e);
        });
    }
  },
};

export default orderService;
